import { Drive } from '../api';
import { getFileSize } from './File';
import { getFileFormatDetails } from './File';
import { InboxFolderListDictionary } from './InboxFolder';
import languageManager from '../helpers/Language';

export const UploadFileStatus = {
    INIT: 'INIT',
    UPLOADING: 'UPLOADING',
    DONE: 'DONE',
    ERROR: 'ERROR'
};

export const UploadManagerEvent = {
    UploadProgress: 'upload_progress'
};

export const UploadFileType = {
    INBOX: 'inbox',
    BUCKET: 'bucket'
};

export class UploadFileUnit {
    constructor(fileReader, parent) {
        this.id = window.crypto.randomUUID();
        this.file = fileReader;
        this.parent = parent;
        this.bucketId = null;
        this._uploadStatus = UploadFileStatus.INIT;
        this._error = null;
        this.uploadType = UploadFileType.INBOX;
        // fileExtension
        const fileExtPos = this.getName().lastIndexOf('.');
        if (fileExtPos > 0) {
            this.precFileExt = this.getName().slice(fileExtPos + 1);
        }
        this.precFileType = getFileFormatDetails(this.getType(), this.getExtension()).guessed;
        this.checkValidity();
    }

    getName() {
        return this.file.name;
    }

    getExtension() {
        return this.precFileExt;
    }

    getLastModified() {
        return this.file.lastModified;
    }

    getLastModifiedDate() {
        return this.file.lastModifiedDate;
    }

    getSize() {
        return this.file.size;
    }

    getSizeFormat() {
        return getFileSize(this.getSize());
    }

    getType() {
        return this.file.type;
    }

    getGuessedType() {
        return this.precFileType;
    }

    getUploadStatus() {
        return this._uploadStatus;
    }

    setInboxFolderCode(inboxCode) {
        this.inboxCode = inboxCode;
        this.precInbox = InboxFolderListDictionary[inboxCode];
        return this;
    }

    setBucketId(bucketId) {
        this.bucketId = bucketId;
        return this;
    }

    setUploadType(type) {
        this.uploadType = type;
        return this;
    }

    getInboxFolderCode() {
        return this.inboxCode;
    }

    getInboxFolder() {
        return this.precInbox || null;
    }

    getProgressPercentage() {
        if (!this.uploadData) {
            return 0;
        }
        return Math.floor(this.uploadData.perc || 0);
    }

    getError() {
        return this._error;
    }

    getFormatedError() {
        const { error_status } = this._error;
        if (!error_status) return null;

        if (error_status === 409 || error_status === 430) {
            return languageManager.T('UPLOAD_FILE_ERROR_LIMIT_EXCEEDED');
        }
        if (error_status >= 500) {
            return languageManager.T('UPLOAD_FILE_ERROR_UNKNOWN');
        }

        return null;
    }
    
    checkValidity() {
        if (this.getSize() > this.parent._uploadSizeMax) {
            this._uploadStatus = UploadFileStatus.ERROR;
            console.log('checkValidity: File size limit exceeded', this.parent);
            this._error = {
                error_status: 430,
                information: 'File size limit exceeded',
                message: 'File size limit exceeded'
            };
        }
    };

    setDriveReference(data) {
        this.driveData = data;
        return this;
    }

    async startUpload() {
        if (this._uploadStatus !== UploadFileStatus.INIT) return;
        const self = this;
        this.uploadData = {
            loaded: 0,
            total: this.getSize()
        };

        const formData = new FormData();
        formData.append('files', this.file);
        let constant = null;

        this._uploadStatus = UploadFileStatus.UPLOADING;
        let res = null;
        const onUploadProgressFunc = (progressEvent) => {
            self.uploadData = {
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                perc: (progressEvent.loaded / progressEvent.total) * 100
            };
            self.parent.notify(UploadManagerEvent.UploadProgress);
        };
        if (this.uploadType === UploadFileType.INBOX) {
            const folderCode = this.getInboxFolder();
            if (folderCode) {
                constant = folderCode.constant;
            }
            res = await Drive.uploadFile(formData, constant, {
                onUploadProgress: onUploadProgressFunc,
            });
        } else {
            res = await Drive.uploadFileToBucket(formData, this.bucketId, {
                onUploadProgress: onUploadProgressFunc,
            });
        }

        // upload completed and successful
        if (res && res.data && res.status === 200) {
            this._uploadStatus = UploadFileStatus.DONE;
            this.parent.notify(UploadManagerEvent.UploadProgress);
        } else { // upload failed
            this._uploadStatus = UploadFileStatus.ERROR;
            this._error = res;
            this.parent.notify(UploadManagerEvent.UploadProgress);
            return;
        }

        const d = res.data;
        this.setDriveReference({
            data: d.data[0],
            resource: d.resource
        });
    } 
}

export class UploadManager extends EventTarget {
    constructor(uploadSizeMax) {
        super();
        this._files = [];
        this.Events = UploadManagerEvent;
        this._uploadSizeMax = uploadSizeMax;
    }

    async addFiles(fileList, autoUpload, inboxCode) {
        let idx = -1;
        const max = fileList.length;
        while (++idx < max) {
            const file = fileList[idx];
            const fileUnit = new UploadFileUnit(file, this);

            if (inboxCode) {
                fileUnit.setInboxFolderCode(inboxCode);
            }
            if (autoUpload) {
                fileUnit.startUpload();
            }
            this._files.push(fileUnit);
        }
    }

    async addBucketFiles(fileList, autoUpload, bucketId) {
        let idx = -1;
        const max = fileList.length;
        while (++idx < max) {
            const file = fileList[idx];
            const fileUnit = new UploadFileUnit(file, this);

            if (bucketId) {
                fileUnit.setUploadType('bucket');
                fileUnit.setBucketId(bucketId);
            }
            if (autoUpload) {
                fileUnit.startUpload();
            }
            this._files.push(fileUnit);
        }
    }

    getFiles() {
        return this._files;
    }

    removeFile(fileId) {
        const idx = this._files.findIndex(f => f.id === fileId);
        if (idx === -1) return this;
        const files = [...this._files];
        files.splice(idx, 1);
        this._files = files;
    }

    notify(type) {
        this.dispatchEvent(new Event(type));
    }

    startUpload() {
        for (const idx in this._files) {
            // FileUnit
            const file = this._files[idx];
            file.startUpload();
        }
    }

    setUploadLimit(num) {
        if (Number.isInteger(num)) {
            this._uploadSizeMax = num;
        }
    }
}
